import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { sort } from 'remeda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { fadeInAndOut } from '~lmo/animations';
import { OrderSummary } from '~lmo/models/order.model';
import { loadPluralMapping } from '~lmo/pluralMappings';
import { LmoCompletedOrdersService } from '~lmo/services/lmo-completed-orders.service';

const completedOrderVirtualIncrement = 10;

interface HeaderWithOrders {
  name: string;
  count: number;
  orders: OrderSummary[];
  isCompleted: boolean;
}

interface OrderCountByMesh {
  meshName: string;
  count: number;
}

@Component({
  animations: [fadeInAndOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-orders-completed',
  styleUrls: ['./lmo-orders-completed.component.scss'],
  templateUrl: './lmo-orders-completed.component.html',
})
export class LmoOrdersCompletedComponent implements OnInit {
  public reachedBottom = false;
  public loadPluralMapping = loadPluralMapping;
  public completedOrdersSearch = new FormControl(this.completedOrdersService.searchTerm);
  public headersWithOrders$: Observable<HeaderWithOrders[]>;
  public completedOrdersEndIndex$$: BehaviorSubject<number> = new BehaviorSubject<number>(
    completedOrderVirtualIncrement,
  );
  public meshCounts$: Observable<OrderCountByMesh[]>;
  constructor(private completedOrdersService: LmoCompletedOrdersService) {}

  public ngOnInit() {
    this.headersWithOrders$ = combineLatest(
      this.completedOrdersEndIndex$$,
      this.completedOrdersService.completedOrders$,
      this.completedOrdersService.cancelledOrders$,
    ).pipe(
      tap(([endIndex, completed, cancelled]) => {
        this.reachedBottom = endIndex > completed.length + cancelled.length;
      }),
      map(([endIndex, completed, cancelled]) => {
        if (endIndex <= completed.length) {
          return [
            {
              count: completed.length,
              isCompleted: true,
              name: 'Completed Orders',
              orders: completed.slice(0, Math.min(endIndex, completed.length)),
            },
          ];
        } else {
          return [
            {
              count: completed.length,
              isCompleted: true,
              name: 'Completed',
              orders: completed.slice(0, Math.min(endIndex, completed.length)),
            },
            {
              count: cancelled.length,
              isCompleted: false,
              name: 'Cancelled Orders',
              orders: cancelled.slice(0, Math.min(endIndex - completed.length, cancelled.length)),
            },
          ];
        }
      }),
    );
    this.meshCounts$ = this.completedOrdersService.completedOrders$.pipe(
      map((orders) => {
        const orderCountMap: Record<number, OrderCountByMesh> = {};
        orders.forEach((order) => {
          if (!orderCountMap[order.meshId]) {
            orderCountMap[order.meshId] = {
              count: 0,
              meshName: order.meshName,
            };
          }
          orderCountMap[order.meshId].count++;
        });
        return sort(Object.values(orderCountMap), (a, b) => a.meshName.localeCompare(b.meshName));
      }),
    );
    this.completedOrdersSearch.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((value) => {
      this.completedOrdersEndIndex$$.next(completedOrderVirtualIncrement);
      this.completedOrdersService.searchBy(value);
      this.completedOrdersService.searchTerm = value;
    });
  }

  public onScroll(event: any) {
    // visible height + pixel scrolled >= total height
    if (!this.reachedBottom && event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
      const currentCompletedOrdersEndIndex = this.completedOrdersEndIndex$$.value;
      this.completedOrdersEndIndex$$.next(currentCompletedOrdersEndIndex + completedOrderVirtualIncrement);
    }
  }
}
